import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, Users, Activity, Clock, TrendingUp, Download,
  RefreshCw, Calendar, FlaskConical, DollarSign, AlertTriangle,
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { NEU } from '../neuStyles';

// ─── Lightweight chart components (no external dependency) ──────────────────

function BarChartSimple({ data, dataKey, nameKey = 'name', color = '#2E8B98', height = 200 }) {
  if (!data || data.length === 0) return <div style={{ color: '#ADB5BD', fontSize: 13, padding: 20 }}>No data available</div>;
  const max = Math.max(...data.map(d => d[dataKey] || 0), 1);
  return (
    <div style={{ height, display: 'flex', alignItems: 'flex-end', gap: 4, padding: '8px 0' }}>
      {data.map((d, i) => {
        const val = d[dataKey] || 0;
        const pct = (val / max) * 100;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 10, color: '#6C757D', fontWeight: 600 }}>{val}</span>
            <div style={{
              width: '100%', maxWidth: 40, height: `${Math.max(pct, 2)}%`,
              background: color, borderRadius: '4px 4px 0 0', minHeight: 4,
              transition: 'height 0.3s ease',
            }} title={`${d[nameKey]}: ${val}`} />
            <span style={{ fontSize: 9, color: '#ADB5BD', textAlign: 'center', lineHeight: 1.2, maxWidth: 48, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d[nameKey]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PieChartSimple({ data, height = 180 }) {
  if (!data || data.length === 0) return <div style={{ color: '#ADB5BD', fontSize: 13, padding: 20 }}>No data available</div>;
  const colors = ['#2E8B98', '#62BB46', '#F26522', '#DC3545', '#256F7A', '#2E8B98', '#14b8a6', '#F26522'];
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  return (
    <div style={{ height, display: 'flex', gap: 16, alignItems: 'center' }}>
      <svg viewBox="0 0 100 100" style={{ width: height * 0.7, height: height * 0.7 }}>
        {data.reduce((acc, d, i) => {
          const pct = d.value / total;
          const startAngle = acc.offset;
          const endAngle = startAngle + pct * 360;
          const largeArc = pct > 0.5 ? 1 : 0;
          const rad1 = (startAngle - 90) * Math.PI / 180;
          const rad2 = (endAngle - 90) * Math.PI / 180;
          const x1 = 50 + 45 * Math.cos(rad1), y1 = 50 + 45 * Math.sin(rad1);
          const x2 = 50 + 45 * Math.cos(rad2), y2 = 50 + 45 * Math.sin(rad2);
          acc.slices.push(
            <path key={i} d={`M50,50 L${x1},${y1} A45,45 0 ${largeArc},1 ${x2},${y2} Z`}
              fill={colors[i % colors.length]} stroke="#fff" strokeWidth="0.5">
              <title>{d.name}: {d.value} ({(pct * 100).toFixed(1)}%)</title>
            </path>
          );
          acc.offset = endAngle;
          return acc;
        }, { slices: [], offset: 0 }).slices}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
        {data.slice(0, 6).map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: colors[i % colors.length], flexShrink: 0 }} />
            <span style={{ color: '#212529' }}>{d.name}</span>
            <span style={{ color: '#ADB5BD', marginLeft: 'auto' }}>{d.value}</span>
          </div>
        ))}
        {data.length > 6 && <span style={{ color: '#ADB5BD', fontSize: 11 }}>+{data.length - 6} more</span>}
      </div>
    </div>
  );
}

function LineChartSimple({ data, lines, height = 200 }) {
  if (!data || data.length === 0) return <div style={{ color: '#ADB5BD', fontSize: 13, padding: 20 }}>No data available</div>;
  const colors = ['#2E8B98', '#62BB46', '#F26522', '#DC3545', '#256F7A', '#2E8B98'];
  const allVals = data.flatMap(d => lines.map(l => d[l] || 0));
  const max = Math.max(...allVals, 1);
  const w = 500, h = height - 40;
  const stepX = data.length > 1 ? w / (data.length - 1) : w;

  return (
    <div style={{ height, overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${w + 40} ${h + 30}`} style={{ width: '100%', height: '100%' }}>
        {lines.map((lineKey, li) => {
          const points = data.map((d, i) => {
            const x = 30 + i * stepX;
            const y = h - ((d[lineKey] || 0) / max) * (h - 10) + 5;
            return `${x},${y}`;
          }).join(' ');
          return <polyline key={lineKey} points={points} fill="none" stroke={colors[li % colors.length]} strokeWidth="2" />;
        })}
        {data.map((d, i) => (
          <text key={i} x={30 + i * stepX} y={h + 18} textAnchor="middle" fontSize="8" fill="#ADB5BD">
            {d.month?.substring(5) || ''}
          </text>
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', fontSize: 11 }}>
        {lines.map((l, i) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 3, background: colors[i % colors.length], borderRadius: 1 }} />
            <span style={{ color: '#6C757D' }}>{l.replace(/_/g, ' ')}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── KPI Card ──────────────────────────────────────────────────────────────

function KPICard({ icon: Icon, label, value, subtitle, color = '#2E8B98' }) {
  return (
    <div style={{
      background: '#F8F9FA', borderRadius: 12, padding: '20px 24px', border: 'none',
      boxShadow: NEU.shadow.sm,
      flex: '1 1 180px', minWidth: 160, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#6C757D', fontWeight: 500 }}>{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#212529', lineHeight: 1 }}>{value}</div>
      {subtitle && <span style={{ fontSize: 11, color: '#ADB5BD' }}>{subtitle}</span>}
    </div>
  );
}

// ─── Main BI Dashboard ─────────────────────────────────────────────────────

function BIDashboard({ user }) {
  const [overview, setOverview] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [encounterTrends, setEncounterTrends] = useState([]);
  const [providerStats, setProviderStats] = useState([]);
  const [labTurnaround, setLabTurnaround] = useState([]);
  const [payerMix, setPayerMix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, demo, trends, providers, labs, payers] = await Promise.all([
        window.zignagenixAPI.getBIOverview(startDate || null, endDate || null),
        window.zignagenixAPI.getBIDemographics(),
        window.zignagenixAPI.getBIEncounterTrends(12),
        window.zignagenixAPI.getBIProviderProductivity(startDate || null, endDate || null),
        window.zignagenixAPI.getBILabTurnaround(),
        window.zignagenixAPI.getBIPayerMix(),
      ]);

      if (ov?.error) throw new Error(ov.message);
      setOverview(ov);
      setDemographics(demo?.error ? null : demo);
      setEncounterTrends(Array.isArray(trends) ? trends : []);
      setProviderStats(Array.isArray(providers) ? providers : []);
      setLabTurnaround(Array.isArray(labs) ? labs : []);
      setPayerMix(Array.isArray(payers) ? payers : []);
    } catch (err) {
      setError(err.message || 'Failed to load BI data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { loadData(); }, [loadData]);

  function exportCSV(data, filename) {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const csv = [headers.join(','), ...data.map(row => headers.map(h => {
      const val = row[h] ?? '';
      return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
    }).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  const sectionStyle = {
    background: '#F8F9FA', borderRadius: 12, border: 'none', boxShadow: NEU.shadow.sm, padding: 24, marginBottom: 16,
  };

  if (loading && !overview) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center', color: '#6C757D' }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
          <p>Loading Business Intelligence data...</p>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: 24, overflowY: 'auto', background: '#F8F9FA', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #2E8B98, #1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BarChart3 size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#212529' }}>Business Intelligence</h1>
            <p style={{ fontSize: 12, color: '#6C757D', margin: 0 }}>Aggregate analytics — No PHI exposed</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PrintButton pageTitle="Business Intelligence" subtitle="Aggregate Analytics" />
          <Calendar size={14} color="#6C757D" />
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 8, border: 'none', fontSize: 12, boxShadow: NEU.shadow.xs, background: '#F8F9FA' }} />
          <span style={{ color: '#ADB5BD', fontSize: 12 }}>to</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 8, border: 'none', fontSize: 12, boxShadow: NEU.shadow.xs, background: '#F8F9FA' }} />
          <button onClick={loadData} style={{
            padding: '6px 12px',
            cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: 'none', boxShadow: '3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#DC3545', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* KPI Cards */}
      {overview && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <KPICard icon={Users} label="Active Patients" value={overview.totalPatients.toLocaleString()} color="#2E8B98" />
          <KPICard icon={Activity} label="Total Encounters" value={overview.totalEncounters.toLocaleString()} subtitle={`${overview.encountersThisMonth} this month`} color="#62BB46" />
          <KPICard icon={Clock} label="Avg Lab Turnaround" value={`${overview.avgLabTurnaroundDays}d`} color="#F26522" />
          <KPICard icon={TrendingUp} label="Active Providers" value={overview.activeProviders} color="#256F7A" />
          <KPICard icon={FlaskConical} label="Abnormal Labs" value={`${overview.abnormalLabPercent}%`} color="#DC3545" />
        </div>
      )}

      {/* Row 1: Demographics + Payer Mix */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212529' }}>Patient Demographics</h3>
          </div>
          {demographics && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <h4 style={{ fontSize: 12, color: '#6C757D', marginBottom: 8 }}>Gender Distribution</h4>
                <PieChartSimple data={demographics.gender} height={140} />
              </div>
              <div>
                <h4 style={{ fontSize: 12, color: '#6C757D', marginBottom: 8 }}>Age Distribution</h4>
                <BarChartSimple data={demographics.age} dataKey="value" color="#256F7A" height={140} />
              </div>
            </div>
          )}
        </div>

        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212529' }}>
              <DollarSign size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Payer Mix
            </h3>
            <button onClick={() => exportCSV(payerMix, 'payer_mix.csv')} style={{
              padding: '4px 8px',
              cursor: 'pointer', fontSize: 11, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4,
            }}><Download size={10} /> CSV</button>
          </div>
          <PieChartSimple data={payerMix} height={180} />
        </div>
      </div>

      {/* Row 2: Encounter Trends */}
      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212529' }}>Encounter Volume Trends (12 months)</h3>
          <button onClick={() => exportCSV(encounterTrends, 'encounter_trends.csv')} style={{
            background: '#F8F9FA', border: 'none', borderRadius: 8, padding: '4px 8px',
            cursor: 'pointer', fontSize: 11, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4, boxShadow: NEU.shadow.xs,
          }}><Download size={10} /> CSV</button>
        </div>
        {encounterTrends.length > 0 ? (
          <LineChartSimple
            data={encounterTrends}
            lines={Object.keys(encounterTrends[0] || {}).filter(k => k !== 'month')}
            height={220}
          />
        ) : (
          <div style={{ color: '#ADB5BD', fontSize: 13, textAlign: 'center', padding: 40 }}>No encounter trend data available</div>
        )}
      </div>

      {/* Row 3: Provider Productivity + Lab Turnaround */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212529' }}>Provider Productivity</h3>
            <button onClick={() => exportCSV(providerStats, 'provider_productivity.csv')} style={{
              padding: '4px 8px',
              cursor: 'pointer', fontSize: 11, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4,
            }}><Download size={10} /> CSV</button>
          </div>
          {providerStats.length > 0 ? (
            <div style={{ fontSize: 13 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8, padding: '8px 0', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', fontWeight: 600, color: '#6C757D', fontSize: 11 }}>
                <span>Provider</span><span>Encounters</span><span>Signed</span><span>Patients</span>
              </div>
              {providerStats.map((p, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8, padding: '8px 0',
                  borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', color: '#212529',
                }}>
                  <span style={{ fontWeight: 500 }}>{p.provider}</span>
                  <span>{p.totalEncounters}</span>
                  <span>{p.signed}</span>
                  <span>{p.uniquePatients}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#ADB5BD', fontSize: 13, textAlign: 'center', padding: 40 }}>No provider data</div>
          )}
        </div>

        <div style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#212529' }}>Lab Turnaround Times</h3>
            <button onClick={() => exportCSV(labTurnaround, 'lab_turnaround.csv')} style={{
              padding: '4px 8px',
              cursor: 'pointer', fontSize: 11, color: '#6C757D', display: 'flex', alignItems: 'center', gap: 4,
            }}><Download size={10} /> CSV</button>
          </div>
          {labTurnaround.length > 0 ? (
            <div style={{ fontSize: 13 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8, padding: '8px 0', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', fontWeight: 600, color: '#6C757D', fontSize: 11 }}>
                <span>Test</span><span>Count</span><span>Avg Days</span><span>Abnormal</span>
              </div>
              {labTurnaround.map((l, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 8, padding: '8px 0',
                  borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)', color: '#212529',
                }}>
                  <span style={{ fontWeight: 500 }}>{l.testName}</span>
                  <span>{l.total}</span>
                  <span>{l.avgDays}</span>
                  <span style={{ color: l.abnormal > 0 ? '#DC3545' : '#212529' }}>{l.abnormal}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: '#ADB5BD', fontSize: 13, textAlign: 'center', padding: 40 }}>No lab turnaround data</div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default BIDashboard;
